import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  countDocumentsCreatedSince,
  fetchDocumentCount,
} from "@/stores/documents/repository/metadata";
import {
  countIssuesCreatedSince,
  countIssuesInScope,
  countIssuesMatching,
  countOverdueIssues,
} from "@/stores/pm/repository";
import { overdueDueBefore } from "@/domains/pm/utils/dueDate";
import {
  useIfcModelCandidates,
  type IfcModelCandidate,
} from "@/domains/buildings/hooks/useIfcModelCandidates";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import type { DocumentChatSidebarTranslations } from "@/lib/i18n/types/documents-sections/chat";
import {
  EMPTY_SUGGESTION_CONTEXT,
  RECENT_WINDOW_DAYS,
  selectChatSuggestions,
  type ChatSuggestionContext,
} from "./chatSuggestions";

type ExampleCopy = DocumentChatSidebarTranslations["examples"];

/** The counted half of the context — one head-count per signal. */
type SuggestionCounts = Pick<
  ChatSuggestionContext,
  | "documentCount"
  | "recentUploadCount"
  | "openIssueCount"
  | "overdueIssueCount"
  | "newIssueCount"
  | "myOpenIssueCount"
>;

const EMPTY_COUNTS: SuggestionCounts = {
  documentCount: null,
  recentUploadCount: null,
  openIssueCount: null,
  overdueIssueCount: null,
  newIssueCount: null,
  myOpenIssueCount: null,
};

interface UseChatExampleQuestionsParams {
  buildingId: string;
  projectId?: string | null;
  scopeName: string;
  copy: ExampleCopy;
  /**
   * Rotates which wording each family contributes. The caller passes the
   * building's chat count, so a new conversation reads differently from the last.
   */
  seed: number;
  /**
   * Only the empty state renders these, so only the empty state pays for them.
   * The chat pill is mounted on every building page — fetching from there would
   * be seven queries nobody looks at.
   */
  enabled: boolean;
}

/** A failed signal is `null`, never a zero — a wrong number is worse than none. */
async function safe<T>(run: () => Promise<T>): Promise<T | null> {
  try {
    return await run();
  } catch {
    return null;
  }
}

function recentWindowStart(): string {
  const since = new Date();
  since.setDate(since.getDate() - RECENT_WINDOW_DAYS);
  return since.toISOString();
}

async function fetchSuggestionCounts(
  buildingId: string,
  projectId: string | null | undefined,
  userId: number | undefined
): Promise<SuggestionCounts> {
  const numericBuildingId = Number(buildingId);
  const numericProjectId = projectId ? Number(projectId) : undefined;
  const since = recentWindowStart();

  const [documents, recentUploads, openIssues, overdueIssues, newIssues, myOpenIssues] =
    await Promise.all([
      safe(() => fetchDocumentCount(buildingId, projectId ?? undefined)),
      safe(() => countDocumentsCreatedSince(buildingId, since, projectId ?? undefined)),
      safe(() =>
        countIssuesInScope(numericBuildingId, {
          buildingProjectId: numericProjectId,
          status: ["in_progress"],
        })
      ),
      safe(() => countOverdueIssues(numericBuildingId, overdueDueBefore(), numericProjectId)),
      safe(() => countIssuesCreatedSince(numericBuildingId, since, numericProjectId)),
      userId == null
        ? Promise.resolve(null)
        : safe(() =>
            countIssuesMatching(
              numericBuildingId,
              { assigneeUserIds: [userId], status: ["in_progress"] },
              numericProjectId
            )
          ),
    ]);

  return {
    documentCount: documents?.count ?? null,
    recentUploadCount: recentUploads,
    openIssueCount: openIssues,
    overdueIssueCount: overdueIssues,
    newIssueCount: newIssues,
    myOpenIssueCount: myOpenIssues,
  };
}

/** The six head-counts for one scope, cached for a minute. */
function useSuggestionCounts(
  buildingId: string,
  projectId: string | null | undefined,
  userId: number | undefined,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["chat-example-context", buildingId, projectId ?? null, userId ?? null],
    queryFn: () => fetchSuggestionCounts(buildingId, projectId, userId),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * Candidates arrive newest-first, so the first one whose lineage has a
 * predecessor is the revision a "what changed?" question should name.
 */
function comparableRevisionFilename(
  candidates: IfcModelCandidate[],
  versionGroupCounts: Map<string, number>
): string | null {
  const comparable = candidates.find(
    (candidate) =>
      candidate.versionGroup !== null && (versionGroupCounts.get(candidate.versionGroup) ?? 0) > 1
  );
  return comparable?.filename ?? null;
}

/**
 * The model half of the context: how many IFCs the scope has, and the newest one
 * a "what changed?" question could name.
 *
 * Already cached for the session by the Differences picker and the 3D viewer, so
 * this is usually a cache read rather than a seventh request.
 */
function useIfcSuggestionSignals(
  numericBuildingId: number | null,
  orgId: string | undefined,
  projectId: string | null | undefined,
  enabled: boolean
) {
  const { candidates, versionGroupCounts, isLoading } = useIfcModelCandidates(
    numericBuildingId,
    orgId,
    projectId ? Number(projectId) : null,
    enabled
  );

  // A disabled query is not "loading" — it is pending with nothing fetching.
  // Until the org resolves, the IFC query has not run, so treating it as
  // settled would render the suggestions without the model family and swap
  // them a moment later.
  const ifcPending = isLoading || !orgId;

  return {
    ifcPending,
    ifcModelCount: ifcPending ? null : candidates.length,
    ifcRevisionFilename: comparableRevisionFilename(candidates, versionGroupCounts),
  };
}

/**
 * The empty state's three questions, chosen from the scope's real aggregate
 * state. Counts come through the same repositories Documents and Issues use, so
 * a number quoted here is a number the user can go and verify.
 */
export function useChatExampleQuestions({
  buildingId,
  projectId,
  scopeName,
  copy,
  seed,
  enabled,
}: UseChatExampleQuestionsParams) {
  const { userProfile } = useAuth();
  const { currentOrganization } = useOrganizationContext();
  const permissions = usePermissions();

  const numericBuildingId = Number(buildingId);
  const hasBuilding = Number.isFinite(numericBuildingId);
  const isActive = enabled && hasBuilding;

  const counts = useSuggestionCounts(buildingId, projectId, userProfile?.id, isActive);
  const { ifcPending, ifcModelCount, ifcRevisionFilename } = useIfcSuggestionSignals(
    hasBuilding ? numericBuildingId : null,
    currentOrganization?.id,
    projectId,
    isActive
  );

  const canUseIssues = permissions.canViewPMTool;
  const canViewBuildingData = hasBuilding
    ? permissions.canViewBuildingDataSidebar(numericBuildingId)
    : false;

  const context = useMemo<ChatSuggestionContext>(() => {
    if (!enabled) return EMPTY_SUGGESTION_CONTEXT;
    return {
      ...(counts.data ?? EMPTY_COUNTS),
      ifcModelCount,
      ifcRevisionFilename,
      canUseIssues,
      canViewBuildingData,
    };
  }, [canUseIssues, canViewBuildingData, counts.data, enabled, ifcModelCount, ifcRevisionFilename]);

  // Nothing renders until the counts land: a question built on half a context
  // would swap for a different one a moment later.
  const pending = !enabled || counts.isPending || ifcPending;
  const questions = useMemo(
    () => (pending ? [] : selectChatSuggestions(copy, scopeName, context, seed)),
    [context, copy, pending, scopeName, seed]
  );

  return { questions };
}
